import { useRef } from 'react'
import { QueryFunctionContext, useQuery } from 'react-query'
import produce, { castImmutable } from 'immer'
import useQueryContext from '../../context/QueryContext'
import apiClient from '../../utils/api'
import {
    TViewMoveAnchor,
    arrangeViewsByIds,
    moveViewToAnchor,
    placeOverviewViewFirst,
    placeRoutineViewAfterRoutines,
} from '../../utils/overview'
import { applyOverviewOrderToRoutines, applyRoutineProgressionToOverview } from '../../utils/routines'
import { TOverviewView, TOverviewViewType, TRoutine, TSupportedView, TSupportedViewItem } from '../../utils/types'
import { useGTMutation, useGTQueryClient } from '../queryUtils'
import { useGetMeetingPreparationTasks } from './meeting-preparation-tasks.hooks'

export const useGetOverviewViews = () => {
    useGetMeetingPreparationTasks()
    return useQuery<TOverviewView[], void>('overview', getOverviewViews)
}
const getOverviewViews = async ({ signal }: QueryFunctionContext) => {
    try {
        const res = await apiClient.get('/overview/views', {
            signal,
            params: {
                ignore_meeting_preparation: true,
            },
        })
        return castImmutable(res.data)
    } catch {
        throw 'getTasks failed'
    }
}

export interface TReorderViewData {
    id: string
    anchor: TViewMoveAnchor
}
interface TPendingMove extends TReorderViewData {
    key: number
}
interface TReorderState {
    // Card order the server has confirmed, as of the start of the current burst of moves.
    confirmedIds: string[]
    // Moves queued or in flight, oldest first. useGTMutation sends them one at a time.
    pending: TPendingMove[]
    nextKey: number
}

// Card order is also routine order, so a move re-labels Current and Up next (and the lock) at once,
// in both the Overview and Routines caches. Moves are anchored to a neighbouring card, so each one
// means the same thing to the server whether or not the moves before it saved. A failed move is
// dropped and the rest replayed on the confirmed order, so it never undoes a later move that saved;
// the refetch waits until the whole queue has drained, so it cannot snap a card back mid-burst.
export const useReorderViews = () => {
    const queryClient = useGTQueryClient()
    const state = useRef<TReorderState>({ confirmedIds: [], pending: [], nextKey: 0 })

    const showPendingOrder = () => {
        const views = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
        if (!views) return
        const ids = state.current.pending.reduce(
            (order, move) => moveViewToAnchor(order, move.id, move.anchor),
            state.current.confirmedIds.map((id) => ({ id }))
        )
        const nextViews = applyRoutineProgressionToOverview(
            arrangeViewsByIds(
                views,
                ids.map(({ id }) => id)
            )
        )
        queryClient.setQueryData('overview', nextViews)
        const routines = queryClient.getImmutableQueryData<TRoutine[]>('routines')
        if (routines) queryClient.setQueryData('routines', applyOverviewOrderToRoutines(routines, nextViews))
    }
    const settle = async (context: unknown) => {
        const key = await Promise.resolve(context)
        const index = state.current.pending.findIndex((move) => move.key === key)
        return index === -1 ? undefined : state.current.pending.splice(index, 1)[0]
    }

    return useGTMutation<unknown, unknown, TReorderViewData, Promise<number | undefined>>((data) => reorderView(data), {
        tag: 'overview',
        invalidateTagsOnSettled: ['overview', 'routines'],
        errorMessage: 'move card',
        onMutate: async ({ id, anchor }: TReorderViewData) => {
            const views = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
            if (!views) return undefined
            if (state.current.pending.length === 0) state.current.confirmedIds = views.map((view) => view.id)
            const key = state.current.nextKey++
            // Recorded before the await so back-to-back moves replay in the order they were made.
            state.current.pending.push({ key, id, anchor })
            // Show the move now: useGTMutation sends the request without waiting for this function.
            showPendingOrder()
            // A cancelled in-flight refetch restores the cache it started from, so apply it again after.
            // Not 'routines': reverting it would also drop an optimistic step tick, and the routines
            // order is re-derived from this cache and refetched once the queue drains.
            await Promise.all([queryClient.cancelQueries('overview'), queryClient.cancelQueries('tasks_v4')])
            showPendingOrder()
            return key
        },
        onSuccess: (_data, _variables, context) => {
            void settle(context).then((move) => {
                if (!move) return
                state.current.confirmedIds = moveViewToAnchor(
                    state.current.confirmedIds.map((id) => ({ id })),
                    move.id,
                    move.anchor
                ).map(({ id }) => id)
                // A refetch that landed while a move was in flight (window focus, another queue) shows
                // an order without it; put the saved and still-pending moves back until the queue's refetch.
                showPendingOrder()
            })
        },
        // useGTMutation shows the "Failed to move card" toast.
        onError: (_error, _variables, context) => {
            void settle(context).then((move) => {
                if (move) showPendingOrder()
            })
        },
    })
}
const reorderView = async ({ id, anchor }: TReorderViewData) => {
    try {
        const res = await apiClient.patch(
            `/overview/views/${id}/`,
            'beforeViewId' in anchor ? { before_view_id: anchor.beforeViewId } : { after_view_id: anchor.afterViewId }
        )
        return castImmutable(res.data)
    } catch {
        throw 'reorderView failed'
    }
}

export const useGetSupportedViews = () => {
    return useQuery<TSupportedView[], void>('overview-supported-views', getSupportedViews)
}
const getSupportedViews = async ({ signal }: QueryFunctionContext) => {
    try {
        const res = await apiClient.get('/overview/supported_views/', { signal })
        return castImmutable(res.data)
    } catch {
        throw 'getSupportedViews failed'
    }
}

export interface TAddViewData {
    optimisticId: string
    supportedView: TSupportedView
    supportedViewIndex: number
    supportedViewItem: TSupportedViewItem
    supportedViewItemIndex: number
}
interface TAddViewPayload {
    type: TOverviewViewType
    task_section_id?: string
    routine_id?: string
}
interface TAddViewReponse {
    id: string
}
export const useAddView = () => {
    const queryClient = useGTQueryClient()
    const { setOptimisticId } = useQueryContext()
    return useGTMutation<TAddViewReponse, unknown, TAddViewData, unknown>(
        ({ supportedView, supportedViewItem }: TAddViewData) => {
            const payload: TAddViewPayload = {
                type: supportedView.type,
                task_section_id: supportedViewItem.task_section_id || undefined,
                routine_id: supportedViewItem.routine_id || undefined,
            }
            return addView(payload)
        },
        {
            tag: 'overview',
            invalidateTagsOnSettled: ['overview', 'overview-supported-views', 'routines'],
            errorMessage: 'add list',
            onMutate: async ({
                optimisticId,
                supportedView,
                supportedViewIndex,
                supportedViewItem,
                supportedViewItemIndex,
            }: TAddViewData) => {
                await Promise.all([
                    queryClient.cancelQueries('overview-supported-views'),
                    queryClient.cancelQueries('overview'),
                ])

                const supportedViews = queryClient.getImmutableQueryData<TSupportedView[]>('overview-supported-views')
                if (supportedViews) {
                    const newSupportedViews = produce(supportedViews, (draft) => {
                        draft[supportedViewIndex].views[supportedViewItemIndex].is_added = true
                        draft[supportedViewIndex].views[supportedViewItemIndex].view_id = optimisticId
                        draft[supportedViewIndex].views[supportedViewItemIndex].optimisticId = optimisticId
                    })
                    queryClient.setQueryData('overview-supported-views', newSupportedViews)
                }

                const views = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
                if (views) {
                    const newViews = produce(views, (draft) => {
                        const optimisticView: TOverviewView = {
                            id: optimisticId,
                            optimisticId,
                            name: supportedViewItem.name,
                            type: supportedView.type,
                            task_section_id: supportedViewItem.task_section_id,
                            routine_id: supportedViewItem.routine_id,
                            is_reorderable: false,
                            logo: supportedView.logo,
                            view_items: [],
                            view_item_ids: [],
                            sources: [],
                            is_linked: true,
                            has_tasks_completed_today: false,
                            status: supportedView.type === 'routine' ? 'empty' : undefined,
                            remaining_count: supportedView.type === 'routine' ? 0 : undefined,
                            streak: supportedView.type === 'routine' ? 0 : undefined,
                        }
                        // Match the server's placement so the card doesn't jump when it saves.
                        return supportedView.type === 'routine'
                            ? placeRoutineViewAfterRoutines(draft as TOverviewView[], optimisticView)
                            : placeOverviewViewFirst(draft as TOverviewView[], optimisticView)
                    })
                    queryClient.setQueryData('overview', newViews)
                }
            },
            onSuccess: (data, { optimisticId, supportedViewIndex, supportedViewItemIndex }) => {
                if (!data) return
                setOptimisticId(optimisticId, data.id)
                const supportedViews = queryClient.getImmutableQueryData<TSupportedView[]>('overview-supported-views')
                if (supportedViews) {
                    const newSupportedViews = produce(supportedViews, (draft) => {
                        draft[supportedViewIndex].views[supportedViewItemIndex].view_id = data.id
                    })
                    queryClient.setQueryData('overview-supported-views', newSupportedViews)
                }
            },
        }
    )
}
const addView = async (data: TAddViewPayload) => {
    try {
        const res = await apiClient.post('/overview/views/', data)
        return castImmutable(res.data)
    } catch {
        throw 'addView failed'
    }
}

interface TRemoveViewData {
    id: string
}
export const useRemoveView = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation(({ id }: TRemoveViewData) => removeView(id), {
        tag: 'overview',
        invalidateTagsOnSettled: ['overview', 'overview-supported-views', 'routines'],
        errorMessage: 'remove list',
        onMutate: async ({ id }) => {
            const supportedViews = queryClient.getImmutableQueryData<TSupportedView[]>('overview-supported-views')
            const views = queryClient.getImmutableQueryData<TOverviewView[]>('overview')
            await Promise.all([
                queryClient.cancelQueries('overview-supported-views'),
                queryClient.cancelQueries('overview'),
            ])

            if (supportedViews) {
                const newSupportedViews = produce(supportedViews, (draft) => {
                    let found = false
                    for (const view of draft) {
                        for (const viewItem of view.views) {
                            if (viewItem.view_id === id) {
                                viewItem.is_added = false
                                viewItem.view_id = ''
                                found = true
                                break
                            }
                        }
                        if (found) break
                    }
                })
                queryClient.setQueryData('overview-supported-views', newSupportedViews)
            }

            if (views) {
                const newViews = produce(views, (draft) => {
                    for (const view of draft) {
                        if (view.id === id) {
                            draft.splice(draft.indexOf(view), 1)
                            break
                        }
                    }
                })
                // A routine without a card drops behind the shown ones, so the next one may become Current.
                queryClient.setQueryData('overview', applyRoutineProgressionToOverview(newViews))
            }
        },
    })
}
const removeView = async (viewId: string) => {
    try {
        await apiClient.delete(`/overview/views/${viewId}/`)
    } catch {
        throw 'removeView failed'
    }
}
